import {
  Button,
  Grid,
  Input,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import * as React from "react";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import useFileUpload from "../hooks/useFileUpload";
import Papa from "papaparse";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useFlash } from "../FlashContext";

interface Props {
  onImport: Function;
}

const ImportSection: React.FC<Props> = (props: any) => {
  const { handleFileUpload } = useFileUpload();

  const [reportType, setReportType] = React.useState<string>("report_type");
  const [providerFile, setProviderFile] = React.useState<any>();
  const [patientEligibilityFile, setPatientEligibilityFile] =
    React.useState<any>();
  const [drugClaimFile, setDrugClaimFile] = React.useState<any>();
  const [medicalClaimFile, setMedicalClaimFile] = React.useState<any>();
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();
  const [processing, setProcessing] = React.useState(false);

  const handleImport = async () => {
    if (!validateFiles()) {
      showToast({
        title: "All Files Required",
        text: "All 4 files are required.",
        type: "error",
      });
      return;
    }
    setProcessing(true);
    const formData = new FormData();
    formData.append("provider_data", providerFile);
    formData.append("patient_eligibility_data", patientEligibilityFile);
    formData.append("drug_claim_data", drugClaimFile);
    formData.append("medical_claim_data", medicalClaimFile);

    const providerFileHeaders: any = await createCsvWithOnlyHeaders(
      providerFile
    );
    const patientEligibilityFileHeaders: any = await createCsvWithOnlyHeaders(
      patientEligibilityFile
    );
    const drugClaimFileHeaders: any = await createCsvWithOnlyHeaders(
      drugClaimFile
    );
    const medicalClaimFileHeaders: any = await createCsvWithOnlyHeaders(
      medicalClaimFile
    );

    const onlyHeadersFormData = new FormData();
    onlyHeadersFormData.append("provider_headers", providerFileHeaders);
    onlyHeadersFormData.append(
      "patient_eligibility_headers",
      patientEligibilityFileHeaders
    );
    onlyHeadersFormData.append("drug_claim_headers", drugClaimFileHeaders);
    onlyHeadersFormData.append(
      "medical_claim_headers",
      medicalClaimFileHeaders
    );

    validateHeaders(formData, onlyHeadersFormData);
  };

  const createCsvWithOnlyHeaders = async (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        delimiter: "", // Leave blank to auto detect delimiter
        complete: (result) => {
          if (!result.data.length) {
            console.log("No data.length");
            console.log(result.data);
            showToast({
              title: "Empty File",
              text: `${file.name}: Wrong file format.`,
              type: "error",
            });
            setProcessing(false);
          } else {
            const headers = Object.keys(result.data[0]);
            const csvContent = Papa.unparse([headers]);
            const blob = new Blob([csvContent], {
              type: file.type,
            });
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });
            resolve(newFile);
          }
        },
        error: (error) => {
          console.log(error);
          showToast({
            title: "Error Reading File",
            text: `${file.name}: Wrong file format.`,
            type: "error",
          });
          setProcessing(false);
        },
      });
    });
  };

  const validateHeaders = (
    formData: FormData,
    onlyHeadersFormData: FormData
  ) => {
    fetch(`/validate_headers`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "X-CSRF-Token": authenticationSetting.csrfToken,
        "X-Frame-Options": "sameorigin",
        "X-XSS-Protection": "1; mode=block",
        "Referrer-Policy": "strict-origin-when-cross-origin",
        "Content-Security-Policy": "default-src 'self'",
      },
      body: onlyHeadersFormData,
    })
      .then((result) => result.json())
      .then(async (result) => {
        if (result.success == false) {
          setProcessing(false);
          console.error(result.error);
          showToast({
            title: "Invalid File Headers",
            text: result.error,
            type: "error",
          });
        } else {
          const objectsMetadata = await handleFileUpload(formData);
          processNdmReport(objectsMetadata);
        }
        setProcessing(false);
      })
      .catch((error) => {
        setProcessing(false);
        console.error(error);
        showToast({
          title: "Error Validating Headers",
          text: error.message,
          type: "error",
        });
      });
  };

  const processNdmReport = (objectsMetadata) => {
    fetch(`/process-ndm-reports`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify(objectsMetadata),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          showToast({
              title: "Import Successful",
            text: result.message,
            type: "success",
          });
          setReportType("report_type");
          props.onImport();
        }
        setProcessing(false);
      })
      .catch((error) => {
        setProcessing(false);
        console.error(error);
        showToast({
          title: "Error Processing Report",
          text: error.message,
          type: "error",
        });
      });
  };

  const validateFiles = () => {
    return (
      !!medicalClaimFile &&
      !!drugClaimFile &&
      !!patientEligibilityFile &&
      !!providerFile
    );
  };

  return (
    <div className="adminFunctions" style={{ paddingRight: "18px" }}>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="container"
      >
        <Grid
          container
          item
          className="userAdminInformation"
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          style={{ paddingBottom: 30 }}
        >
          <Grid item xs={12}>
            <Stack
              direction="row"
              columnGap="12px"
              alignItems="center"
              style={{ paddingLeft: 16 }}
            >
              <Link
                style={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                }}
                href={`/data-tools`}
              >
                <ArrowBackIosNewIcon
                  style={{
                    borderRadius: "3px",
                    padding: "8px",
                    color: "black",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    fontSize: "14px",
                  }}
                />
              </Link>
              <h3>Payer Patient Data Import</h3>
            </Stack>
            <div
              className="divider-orange"
              style={{ margin: "0px", width: "100%" }}
            ></div>
            <Grid container style={{ paddingLeft: 16 }}>
              <Grid item xs={12}>
                <p>Use the payer patient data import to import patient data</p>
              </Grid>
              {reportType === "report_type" && (
                <>
                  <Grid item xs={12} style={{ marginTop: 10 }}>
                    <b>Select Payer Patient Data Import Type</b>
                  </Grid>
                  <TextField
                    id="role"
                    className="the-field"
                    style={{ width: "30%", marginTop: 10 }}
                    value={reportType}
                    variant="outlined"
                    size="small"
                    onChange={(event) => {
                      setReportType(event.target.value.toString());
                    }}
                    select
                  >
                    <MenuItem value={"report_type"}>Report Type</MenuItem>
                    <MenuItem value="ndm_report">
                      North Dakota Medicaid
                    </MenuItem>
                  </TextField>
                  <Grid item xs={12} style={{ marginTop: 10 }}>
                    <Link
                      className="menuLink"
                      style={{
                        width: "200px",
                        backgroundColor: "#e0e0e0",
                        color: "#8b8b8b",
                      }}
                      onClick={undefined}
                    >
                      Import
                    </Link>
                  </Grid>
                </>
              )}
              {reportType === "ndm_report" && (
                <>
                  <Grid
                    container
                    item
                    xs={12}
                    style={{ marginTop: 10 }}
                    spacing={2}
                  >
                    <Grid item md={6}>
                      <p>
                        <b>Select Provider File</b>
                      </p>
                      <div className="import-button-continer">
                        <Grid container>
                          <Grid item xs={6} className="import-file-name">
                            <p>
                              {!!providerFile
                                ? `${providerFile.name?.substring(0, 30)}...`
                                : "Please select a file"}
                            </p>
                          </Grid>
                          <Grid item xs={6}>
                            <input
                              type="file"
                              accept={".csv"}
                              onChange={(event) => {
                                setProviderFile(event.target.files[0]);
                              }}
                              style={{ display: "none", height: "42px" }}
                              id="file-input"
                            />
                            <label
                              htmlFor="file-input"
                              style={{ width: "50%" }}
                            >
                              <Button className="btn-import" component="span">
                                Browse Files
                              </Button>
                            </label>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                    <Grid item md={6}>
                      <p>
                        <b>Select Patient Eligibility File</b>
                      </p>
                      <div className="import-button-continer">
                        <Grid container>
                          <Grid item xs={6} className="import-file-name">
                            <p>
                              {!!patientEligibilityFile
                                ? `${patientEligibilityFile.name?.substring(
                                    0,
                                    30
                                  )}...`
                                : "Please select a file"}
                            </p>
                          </Grid>
                          <Grid item xs={6}>
                            <input
                              type="file"
                              accept={".csv"}
                              onChange={(event) => {
                                setPatientEligibilityFile(
                                  event.target.files[0]
                                );
                              }}
                              style={{ display: "none", height: "42px" }}
                              id="patient-eligibility-file-input"
                            />
                            <label
                              htmlFor="patient-eligibility-file-input"
                              style={{ width: "50%" }}
                            >
                              <Button className="btn-import" component="span">
                                Browse Files
                              </Button>
                            </label>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                    <Grid item md={6}>
                      <p>
                        <b>Select Drug Claim File</b>
                      </p>
                      <div className="import-button-continer">
                        <Grid container>
                          <Grid item xs={6} className="import-file-name">
                            <p>
                              {!!drugClaimFile
                                ? `${drugClaimFile.name?.substring(0, 30)}...`
                                : "Please select a file"}
                            </p>
                          </Grid>
                          <Grid item xs={6}>
                            <input
                              type="file"
                              accept={".csv"}
                              onChange={(event) => {
                                setDrugClaimFile(event.target.files[0]);
                              }}
                              style={{ display: "none", height: "42px" }}
                              id="drug-claim-file-input"
                            />
                            <label
                              htmlFor="drug-claim-file-input"
                              style={{ width: "50%" }}
                            >
                              <Button className="btn-import" component="span">
                                Browse Files
                              </Button>
                            </label>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                    <Grid item md={6}>
                      <p>
                        <b>Select Medical Claim File</b>
                      </p>
                      <div className="import-button-continer">
                        <Grid container>
                          <Grid item xs={6} className="import-file-name">
                            <p>
                              {!!medicalClaimFile
                                ? `${medicalClaimFile.name?.substring(
                                    0,
                                    30
                                  )}...`
                                : "Please select a file"}
                            </p>
                          </Grid>
                          <Grid item xs={6}>
                            <input
                              type="file"
                              accept={".csv"}
                              onChange={(event) => {
                                setMedicalClaimFile(event.target.files[0]);
                              }}
                              style={{ display: "none", height: "42px" }}
                              id="medical-claim-file-input"
                            />
                            <label
                              htmlFor="medical-claim-file-input"
                              style={{ width: "50%" }}
                            >
                              <Button className="btn-import" component="span">
                                Browse Files
                              </Button>
                            </label>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    justifyContent="center"
                    style={{ marginTop: 30 }}
                  >
                    <Grid item>
                      <Link
                        className="btn-cancel"
                        style={{ width: "200px" }}
                        onClick={() => setReportType("report_type")}
                      >
                        Cancel
                      </Link>
                    </Grid>
                    <Grid item>
                      <Link
                        className={processing ? "btn-disabled" : "btn-regular"}
                        style={{ width: "200px" }}
                        onClick={processing ? undefined : handleImport}
                      >
                        Import
                      </Link>
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default ImportSection;
